import { matchPath } from 'react-router-dom';
import { AI_SCREEN_CONTEXT_ROUTES } from 'constants/aiScreenContext';

import type { Location, Params } from 'react-router-dom';
import type IAiScreenContext from './interfaces/IAiScreenContext';
import type {
  IAiScreenContextEntity, IAiSubScreenContext, TAiScreenContextInput,
} from './interfaces/IAiScreenContext';

type TRouteMatch = {
  screen: string,
  params: Params,
  score: number,
};

/** The part of the location the context is read from. */
export type TAiScreenLocation = Pick<Location, 'pathname' | 'search'>;

const NO_SUB_SCREENS: IAiSubScreenContext[] = [];

/**
 * Ranks a route pattern by how specific it is: a longer path wins, and between paths of the same
 * length the one with fewer parameters wins, being the more literal of the two.
 */
const scoreRoute = (path: string): number => {
  const segments = path.split('/').filter(Boolean);
  const dynamic = segments.filter((segment) => segment.startsWith(':')).length;

  return segments.length * 10 - dynamic;
};

const paramsToEntities = (params: Params): IAiScreenContextEntity[] => Object
  .entries(params)
  .filter((entry): entry is [string, string] => Boolean(entry[1]))
  .map(([type, id]) => ({ type, id }));

/** Drops repeats, keeping the first of each type and id, so the route floor is not doubled. */
const mergeEntities = (
  base: IAiScreenContextEntity[],
  extra: IAiScreenContextEntity[],
): IAiScreenContextEntity[] => {
  const merged = [...base, ...extra];

  return merged.filter((entity, index) => merged.findIndex(
    (other) => other.type === entity.type && `${other.id}` === `${entity.id}`,
  ) === index);
};

/**
 * The context every screen has for free: which screen it is, where it is, and what its route
 * names. The url is the path and query as the browser has them, with no origin in front.
 */
export const routeAiScreenContext = ({ pathname, search }: TAiScreenLocation): IAiScreenContext => {
  const url = `${pathname}${search}`;
  const best = AI_SCREEN_CONTEXT_ROUTES.reduce<TRouteMatch | null>((current, { path, screen }) => {
    const match = matchPath({ path, end: false }, pathname);

    if (!match) {
      return current;
    }

    const score = scoreRoute(path);

    return !current || score > current.score ? { screen, params: match.params, score } : current;
  }, null);

  // An unregistered route still names itself, so the assistant is never left with nothing.
  return best
    ? {
      screen: best.screen, url, entities: paramsToEntities(best.params), subScreens: NO_SUB_SCREENS,
    }
    : { screen: pathname, url, subScreens: NO_SUB_SCREENS };
};

/**
 * Resolves what the assistant knows: the route floor, enriched by the screen that registered last,
 * with whatever sub-screens are open stacked on top.
 *
 * The screen wins on every field it sets. Entities are the exception — those add up, so a screen
 * keeps what its route already named. Sub-screens never replace the screen: a modal is read as
 * open over the page, not instead of it.
 *
 * Read before you change the merge rules, and before you add a field:
 * @see [9.2 Screen context (frontend)](../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 * @see [12.9 Add a chat context field](../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
export default function resolveAiScreenContext(
  location: TAiScreenLocation,
  registered?: TAiScreenContextInput | null,
  subScreens: IAiSubScreenContext[] = NO_SUB_SCREENS,
): IAiScreenContext {
  const route = routeAiScreenContext(location);

  if (!registered) {
    return { ...route, subScreens };
  }

  return {
    screen: registered.screen || route.screen,
    url: route.url,
    title: registered.title,
    entities: mergeEntities(route.entities || [], registered.entities || []),
    customData: registered.customData,
    subScreens,
  };
}
